import { useEffect, useState } from "react";
import { Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { rgApi } from "@/lib/rgApi";

/**
 * BreakfastConfigBlock — Réglages du formulaire petit-déjeuner (Réglages).
 * Permet d'éditer titre, intro, jours de service, horaires, choix (œufs,
 * boissons, beurres, confitures), intitulés et champs obligatoires.
 * Le nombre de sections « Voyageur » reste automatique (selon la réservation).
 * Ne modifie JAMAIS les réponses déjà reçues.
 */

const DAYS = [
  { v: 0, label: "Lun" }, { v: 1, label: "Mar" }, { v: 2, label: "Mer" },
  { v: 3, label: "Jeu" }, { v: 4, label: "Ven" }, { v: 5, label: "Sam" }, { v: 6, label: "Dim" },
];

const REQUIRED_FIELDS = [
  { k: "time", label: "Heure" },
  { k: "eggs", label: "Œufs" },
  { k: "drink", label: "Boisson" },
  { k: "butter", label: "Beurre" },
  { k: "jams", label: "Confitures" },
];

const LABEL_FR = {
  name: "Nom", date: "Date", time: "Heure", eggs: "Œufs",
  drink: "Boisson", butter: "Beurre", jams: "Confitures", comments: "Remarques",
};

export default function BreakfastConfigBlock() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { rgApi.getBreakfastConfig().then(setCfg).catch(() => {}); }, []);

  if (!cfg) return null;

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const setLabel = (k, v) => setCfg((c) => ({ ...c, labels: { ...c.labels, [k]: v } }));
  const setRequired = (k, v) => setCfg((c) => ({ ...c, required: { ...c.required, [k]: v } }));
  const toCSV = (arr) => (arr || []).join(", ");
  const fromCSV = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
  const toggleDay = (v) => setCfg((c) => {
    const days = c.service_days || [];
    return { ...c, service_days: days.includes(v) ? days.filter((d) => d !== v) : [...days, v].sort() };
  });

  const save = async () => {
    setBusy(true);
    try {
      const saved = await rgApi.saveBreakfastConfig(cfg);
      setCfg(saved);
      toast.success("Réglages du petit-déjeuner enregistrés");
    } catch (e) {
      toast.error("Échec de l'enregistrement", { description: e?.message });
    } finally { setBusy(false); }
  };

  const Row = ({ label, children }) => (
    <div style={{ marginBottom: 12 }}>
      <label className="rg-tiny" style={{ display: "block", marginBottom: 4, fontWeight: 600, color: "var(--rg-text-2)" }}>{label}</label>
      {children}
    </div>
  );

  return (
    <div className="rg-card" style={{ marginBottom: 16 }} data-testid="rg-breakfast-config-section">
      <h3 className="rg-h3">⚙️ Réglages du formulaire petit-déjeuner</h3>
      <p className="rg-muted" style={{ marginBottom: 12 }}>
        Personnalise le formulaire reçu par les voyageurs. Le nombre de sections « Voyageur » s&apos;adapte automatiquement à chaque réservation. Les réponses déjà reçues ne sont jamais modifiées.
      </p>

      <Row label="Titre">
        <input className="rg-input" value={cfg.title || ""} onChange={(e) => set({ title: e.target.value })} data-testid="rg-bfcfg-title" />
      </Row>
      <Row label="Texte d'introduction (optionnel, affiché sous le titre)">
        <textarea className="rg-textarea" value={cfg.intro || ""} onChange={(e) => set({ intro: e.target.value })} data-testid="rg-bfcfg-intro" style={{ minHeight: 60 }} />
      </Row>

      <Row label="Jours de service">
        <div className="rg-row" style={{ gap: 6, flexWrap: "wrap" }}>
          {DAYS.map((d) => (
            <button key={d.v} type="button" className={`rg-btn small ${(cfg.service_days || []).includes(d.v) ? "" : "ghost"}`}
              onClick={() => toggleDay(d.v)} data-testid={`rg-bfcfg-day-${d.v}`}>{d.label}</button>
          ))}
        </div>
      </Row>

      <Row label="Horaires proposés (séparés par des virgules)">
        <input className="rg-input" value={toCSV(cfg.times)} onChange={(e) => set({ times: fromCSV(e.target.value) })} data-testid="rg-bfcfg-times" />
      </Row>
      <Row label="Choix d'œufs">
        <input className="rg-input" value={toCSV(cfg.egg_choices)} onChange={(e) => set({ egg_choices: fromCSV(e.target.value) })} data-testid="rg-bfcfg-eggs" />
      </Row>
      <Row label="Boissons">
        <input className="rg-input" value={toCSV(cfg.drinks)} onChange={(e) => set({ drinks: fromCSV(e.target.value) })} data-testid="rg-bfcfg-drinks" />
      </Row>
      <Row label="Beurres">
        <input className="rg-input" value={toCSV(cfg.butters)} onChange={(e) => set({ butters: fromCSV(e.target.value) })} data-testid="rg-bfcfg-butters" />
      </Row>
      <Row label="Confitures">
        <input className="rg-input" value={toCSV(cfg.jams)} onChange={(e) => set({ jams: fromCSV(e.target.value) })} data-testid="rg-bfcfg-jams" />
      </Row>
      <Row label="Nombre de confitures à choisir">
        <input type="number" min={1} max={(cfg.jams || []).length || 3} className="rg-input" style={{ width: 90 }}
          value={cfg.jam_count || 2} onChange={(e) => set({ jam_count: Math.max(1, parseInt(e.target.value) || 1) })} data-testid="rg-bfcfg-jamcount" />
      </Row>

      <Row label="Intitulés des questions">
        <div style={{ display: "grid", gap: 6 }}>
          {Object.entries(cfg.labels || {}).map(([k, v]) => (
            <div key={k} className="rg-row" style={{ gap: 8, alignItems: "center" }}>
              <span className="rg-tiny" style={{ width: 90, color: "var(--rg-text-3)" }}>{LABEL_FR[k] || k}</span>
              <input className="rg-input" value={v} onChange={(e) => setLabel(k, e.target.value)} data-testid={`rg-bfcfg-label-${k}`} style={{ flex: 1 }} />
            </div>
          ))}
        </div>
      </Row>

      <Row label="Champs obligatoires">
        <div className="rg-row" style={{ gap: 10, flexWrap: "wrap" }}>
          {REQUIRED_FIELDS.map((f) => (
            <label key={f.k} className="rg-row" style={{ gap: 4, alignItems: "center", fontSize: 13 }}>
              <input type="checkbox" checked={!!(cfg.required || {})[f.k]} onChange={(e) => setRequired(f.k, e.target.checked)} data-testid={`rg-bfcfg-req-${f.k}`} />
              {f.label}
            </label>
          ))}
        </div>
      </Row>

      <div className="rg-row" style={{ gap: 8, marginTop: 8 }}>
        <button className="rg-btn" onClick={save} disabled={busy} data-testid="rg-bfcfg-save">
          <Save size={13} /> {busy ? "Enregistrement…" : "Enregistrer les réglages"}
        </button>
        <button className="rg-btn ghost" onClick={() => rgApi.getBreakfastConfig().then(setCfg)} disabled={busy} data-testid="rg-bfcfg-reload">
          <RotateCcw size={13} /> Recharger
        </button>
      </div>
    </div>
  );
}
